import {
  AuthMethod,
  BillingStatus,
  Company_Read as CompanyProto,
  Company_Read_Nested,
  Company_Read_Typeahead,
  Company_Update,
  CompanyType,
  Currency,
  DateFormat,
  Language,
  OrderUnitOfMeasure,
  SaasBillingPlan,
  SystemOfMeasure,
  TimeFormat,
  TimeZone,
  UnitOfDistance,
  UsageStatus,
  WorkWeekStartDay,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil, isUndefined } from 'lodash-es';

import { data as enums, DEFAULT_COUNTRY_CODE } from '~constants/enums';
import { AddressFormValue, AddressItem } from '~hooks/useAddress/models/AddressItem';
import {
  CompanyContactFormValue,
  ContactItem,
} from '~hooks/useContact/models/ContactItem';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

// Everything `updateCompany` accepts. Two very different callers share it: the full
// Settings > General form (CompanyFormValues), and the ~9 single-setting toggles that
// pass `{ id, theOneFieldTheyChanged }`. Every field is therefore optional, and
// `deparseUpdate` MUST NOT emit a key the caller did not supply — see the
// presence checks below.
//
// This type exists because `deparseUpdate(proto: any)` is what let CUS-447 ship: the
// contacts arrive as `{ firstName, lastName }` but were serialized by a function that
// reads `name`, and nothing checked.
export interface CompanyUpdateInput {
  address?: Nullable<AddressItem | AddressFormValue>;
  aiReviewLoads?: boolean;
  aiReviewLoadsCustomInstructions?: Nullable<string>;
  aiTicketScanningCustomInstructions?: Nullable<string>;
  allowedOrderUnitsOfMeasure?: OrderUnitOfMeasure[];
  autoCompleteOrder?: boolean;
  billingContact?: Nullable<ContactItem | CompanyContactFormValue>;
  billingStatus?: Nullable<BillingStatus>;
  companyType?: Nullable<CompanyType>;
  currency?: Nullable<Currency>;
  dateFormat?: Nullable<DateFormat>;
  defaultDropoffOnsiteMinutes?: number;
  defaultGeofenceDelaySeconds?: number;
  defaultLat?: Nullable<number>;
  defaultLon?: Nullable<number>;
  defaultProjectTerms?: Nullable<string>;
  defaultPaymentTermId?: Nullable<string>;
  doingBusinessName?: string;
  defaultPickupOnsiteMinutes?: number;
  fuelSurcharge?: string | number;
  id: string;
  isTreadCustomer?: boolean;
  language?: Nullable<Language>;
  legalName?: string;
  primaryContact?: Nullable<ContactItem | CompanyContactFormValue>;
  saasBillingPlan?: Nullable<SaasBillingPlan>;
  siteRadius?: Nullable<number>;
  systemOfMeasure?: Nullable<SystemOfMeasure>;
  timeFormat?: Nullable<TimeFormat>;
  timeZone?: Nullable<TimeZone>;
  unitOfDistance?: { value: UnitOfDistance };
  usageStatus?: Nullable<UsageStatus>;
  // `AutocompleteFieldOption.value` is a plain string; `_entity` is the typed one.
  workWeekStartDay?: { _entity: WorkWeekStartDay };
}

// `auto_complete_order` is accepted by CompanyService::Update::PERMITTED_PARAMS but is
// missing from `Company_Update` in the API spec (it is only on `Company_Create`). The
// Settings > Configuration auto-complete toggle depends on it, so the gate is widened
// rather than the key dropped.
// https://linear.app/treadapp/issue/TRE-16084/api-spec-company-update-is-missing-auto-complete-order
type CompanyUpdateBody = Company_Update & { auto_complete_order?: boolean };

export class Company {
  public static parse(proto: CompanyProto): Company {
    return new Company(
      proto?.id ?? '',
      proto?.created_at ?? null,
      proto.parent_company ? CompanyBasic.parse(proto.parent_company) : null,
      proto.legal_name ?? '',
      proto?.dba_name ?? '',
      proto?.address ? AddressItem.parse(proto.address) : null,
      proto.company_type ?? null,
      proto?.tread_customer ?? '',
      proto?.tread_id ?? '',
      proto?.language ?? enums.language.default,
      proto?.date_format ?? enums.date_format.default,
      proto?.time_format ?? enums.time_format.default,
      proto?.system_of_measure ?? enums.system_of_measure.default,
      proto?.unit_of_distance ?? enums.unit_of_distance.default,
      proto.currency ?? enums.currency.default,
      proto?.time_zone ?? enums.time_zone.default,
      proto?.primary_contact ? ContactItem.parse(proto.primary_contact) : null,
      proto?.billing_contact ? ContactItem.parse(proto.billing_contact) : null,
      proto.logo_url ?? null,
      proto.logo_preview_url ?? null,
      proto?.usage_status ?? enums.usage_status.default,
      proto?.billing_status ?? enums.billing_status.default,
      proto?.saas_billing_plan ?? enums.saas_billing_plan.default,
      proto?.fuel_surcharge_percentage ?? 0,
      proto.auth_methods,
      proto.auto_complete_order,
      proto.ai_review_loads ?? false,
      proto.ai_review_loads_custom_instructions ?? null,
      proto.ai_ticket_scanning_custom_instructions ?? null,
      proto.default_project_terms ?? null,
      proto.default_payment_term_id ?? null,
      parseFiniteNumber(proto?.default_lat),
      parseFiniteNumber(proto?.default_lon),
      Number(proto.site_radius) || null,
      proto.default_geofence_delay_seconds ?? 0,
      proto.allowed_order_units_of_measure ?? [],
      proto?.work_week_start_day ?? enums.work_week_start_day.default,
      proto?.default_pickup_onsite_minutes ?? null,
      proto?.default_dropoff_onsite_minutes ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparseUpdate(proto: CompanyUpdateInput) {
    const data = {
      legal_name: proto.legalName || undefined,
      // `dba_name` is nullable in the spec, so a blank value must clear it — but only
      // when the caller actually supplied the field. A bare `|| null` would wipe the
      // DBA name every time someone flipped an unrelated single-setting toggle.
      dba_name:
        'doingBusinessName' in proto ? proto.doingBusinessName || null : undefined,
      address: proto.address ? AddressItem.deparseUpdate(proto.address) : undefined,
      company_type: proto.companyType || undefined,
      primary_contact: proto.primaryContact
        ? ContactItem.deparseCompany(proto.primaryContact)
        : undefined,
      billing_contact: proto.billingContact
        ? ContactItem.deparseCompany(proto.billingContact)
        : undefined,
      // Omit unless explicitly provided — sending a hardcoded default here would silently
      // reset these fields on any partial-DTO update (e.g. a Settings setting that only
      // sends `{ id, oneFieldChanged }`). Saw this surface when default_project_terms
      // saves reset non-English companies' language to "en".
      tread_customer: proto.isTreadCustomer ?? undefined,
      language: proto.language || undefined,
      time_format: proto.timeFormat || undefined,
      date_format: proto.dateFormat || undefined,
      system_of_measure: proto.systemOfMeasure || undefined,
      unit_of_distance: proto.unitOfDistance?.value || undefined,
      currency: proto.currency || undefined,
      time_zone: proto.timeZone || undefined,
      usage_status: proto.usageStatus || undefined,
      billing_status: proto.billingStatus || undefined,
      saas_billing_plan: proto.saasBillingPlan || undefined,
      fuel_surcharge_percentage: isUndefined(proto.fuelSurcharge)
        ? undefined
        : Number(proto.fuelSurcharge) || 0,
      default_lat: isNil(proto.defaultLat) ? undefined : proto.defaultLat.toString(),
      default_lon: isNil(proto.defaultLon) ? undefined : proto.defaultLon.toString(),
      auto_complete_order: proto.autoCompleteOrder,
      ai_review_loads: proto.aiReviewLoads,
      ai_review_loads_custom_instructions: proto.aiReviewLoadsCustomInstructions,
      ai_ticket_scanning_custom_instructions: proto.aiTicketScanningCustomInstructions,
      default_project_terms: proto.defaultProjectTerms,
      default_payment_term_id: proto.defaultPaymentTermId,
      site_radius: proto.siteRadius,
      default_geofence_delay_seconds: isUndefined(proto.defaultGeofenceDelaySeconds)
        ? undefined
        : proto.defaultGeofenceDelaySeconds || 0,
      allowed_order_units_of_measure: proto.allowedOrderUnitsOfMeasure,
      work_week_start_day: proto.workWeekStartDay?._entity || undefined,
      default_pickup_onsite_minutes: proto.defaultPickupOnsiteMinutes,
      default_dropoff_onsite_minutes: proto.defaultDropoffOnsiteMinutes,
    } satisfies CompanyUpdateBody;

    return data;
  }
  public get id(): string {
    return this._id;
  }

  public get createdAt(): string {
    return this._created_at;
  }

  public get legalName(): string {
    return this._legal_name;
  }

  public get parentCompany(): Nullable<CompanyBasic> {
    return this._parentCompany;
  }

  public get dbaName(): string {
    return this._dba_name;
  }

  public get isTreadCustomer(): boolean {
    return this._tread_customer;
  }

  public get address(): Nullable<AddressItem> {
    return this._address;
  }

  public get primaryContact(): Nullable<ContactItem> {
    return this._primary_contact;
  }

  public get billingContact(): Nullable<ContactItem> {
    return this._billing_contact;
  }

  public get timeZone() {
    return this._time_zone;
  }

  public get companyType(): Nullable<CompanyType> {
    return this._company_type;
  }
  public get language(): Language {
    return this._language;
  }

  public get timeFormat() {
    return this._time_format;
  }

  public get dateFormat() {
    return this._date_format;
  }

  public get unitOfDistance() {
    return this._unit_of_distance;
  }
  public get isFeet(): boolean {
    return this._unit_of_distance === UnitOfDistance.MILE;
  }
  public get isMeters(): boolean {
    return this._unit_of_distance === UnitOfDistance.KILOMETER;
  }
  public get systemOfMeasure() {
    return this._system_of_measure;
  }

  public get currency() {
    return this._currency;
  }
  public get logoUrl(): Nullable<string> {
    return this._logo_url;
  }

  public get logoPreviewUrl(): Nullable<string> {
    return this._logo_preview_url;
  }

  public get usageStatus(): Nullable<UsageStatus> {
    return this._usage_status;
  }

  public get billingStatus(): Nullable<BillingStatus> {
    return this._billing_status;
  }

  public get saasBillingPlan(): Nullable<SaasBillingPlan> {
    return this._saas_billing_plan;
  }

  public get treadId(): string {
    return this._tread_id;
  }

  public get fuelSurcharge(): string | number {
    return this._fuel_surcharge_percentage;
  }
  public set fuelSurcharge(value: string | number) {
    this._fuel_surcharge_percentage = Number(value);
  }
  public get country(): string {
    return this._address?.country?.code ?? DEFAULT_COUNTRY_CODE;
  }
  public get authMethods() {
    return this._auth_methods;
  }
  public get autoCompleteOrder() {
    return this._auto_complete_order;
  }
  public get aiReviewLoads() {
    return this._ai_review_loads;
  }
  public get aiReviewLoadsCustomInstructions() {
    return this._ai_review_loads_custom_instructions;
  }
  public get aiTicketScanningCustomInstructions() {
    return this._ai_ticket_scanning_custom_instructions;
  }
  public get defaultProjectTerms(): Nullable<string> {
    return this._default_project_terms;
  }
  public get defaultPaymentTermId() {
    return this._default_payment_term_id;
  }
  public get siteRadius() {
    return this._site_radius;
  }

  public get defaultLat() {
    return this._default_lat;
  }

  public get defaultLon() {
    return this._default_lon;
  }

  public get defaultGeofenceDelaySeconds() {
    return this._default_geofence_delay_seconds;
  }

  public get allowedOrderUnitsOfMeasure() {
    return this._allowed_order_units_of_measure;
  }

  public get workWeekStartDay() {
    return this._work_week_start_day;
  }

  public get defaultPickupOnsiteMinutes() {
    return this._default_pickup_onsite_minutes;
  }

  public get defaultDropoffOnsiteMinutes() {
    return this._default_dropoff_onsite_minutes;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _created_at: string,
    private _parentCompany: Nullable<CompanyBasic>,

    private _legal_name: string,
    private _dba_name: string,
    private _address: Nullable<AddressItem>,

    private _company_type: CompanyType,

    private _tread_customer: boolean,
    private _tread_id: string,

    private _language: Language,
    private _date_format: DateFormat,
    private _time_format: TimeFormat,
    private _system_of_measure: SystemOfMeasure,
    private _unit_of_distance: UnitOfDistance,
    private _currency: Currency,
    private _time_zone: TimeZone,

    private _primary_contact: Nullable<ContactItem>,
    private _billing_contact: Nullable<ContactItem>,
    private _logo_url: Nullable<string>,
    private _logo_preview_url: Nullable<string>,
    private _usage_status: Nullable<UsageStatus>,
    private _billing_status: Nullable<BillingStatus>,
    private _saas_billing_plan: Nullable<SaasBillingPlan>,
    private _fuel_surcharge_percentage: string | number,
    private _auth_methods: Array<AuthMethod>,
    private _auto_complete_order: boolean,
    private _ai_review_loads: boolean,
    private _ai_review_loads_custom_instructions: Nullable<string>,
    private _ai_ticket_scanning_custom_instructions: Nullable<string>,
    private _default_project_terms: Nullable<string>,
    private _default_payment_term_id: Nullable<string>,
    private _default_lat: Nullable<number>,
    private _default_lon: Nullable<number>,
    private _site_radius: Nullable<number>,
    private _default_geofence_delay_seconds: Nullable<number>,
    private _allowed_order_units_of_measure: OrderUnitOfMeasure[],
    private _work_week_start_day: WorkWeekStartDay,
    private _default_pickup_onsite_minutes: Nullable<number>,
    private _default_dropoff_onsite_minutes: Nullable<number>,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class CompanyBasic {
  public static parse(proto: Company_Read_Nested): CompanyBasic {
    return new CompanyBasic(
      proto.id,
      proto.legal_name,
      proto.company_type,
      proto.tread_id,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public get id(): string {
    return this._id;
  }
  public get legalName(): string {
    return this._legal_name;
  }
  public get companyType(): string {
    return this._company_type;
  }
  public get treadId(): string {
    return this._tread_id;
  }
  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _legal_name: string,
    private _company_type: string,
    private _tread_id: string,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class CompanyTypeahead {
  constructor(
    private _id: string,
    private _legal_name: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get legalName() {
    return this._legal_name;
  }

  public static parse(proto: Company_Read_Typeahead) {
    return new CompanyTypeahead(proto.id, proto.legal_name);
  }
}
